import { Implements, Service, ServiceLifetime } from '@gitlab/needle';
import type { AIContextItem } from '@gitlab-org/ai-context';
import type { UserAction } from '../../backend/backend';
import { PresentationPolicy, promptGoal } from '../../backend/presentation_policy';
import { ParsedCliInput } from '../../parse';

// Interactive resume: the `/new` hint is actionable in the TUI.
const RESUMED_SESSION_ENDED_MESSAGE =
  'Previous session has ended and cannot be resumed. Start a new conversation with /new.';

// Interactive-only: headless is a single turn with no `/new`.
const SESSION_ENDED_START_NEW_MESSAGE =
  'This session has ended. Start a new conversation with /new.';

/**
 * Interactive TUI. A user is present: the system context is seeded onto the
 * first message only, and a dead session recovers via `/new`.
 */
@Implements(PresentationPolicy)
@Service({
  dependencies: [ParsedCliInput],
  lifetime: ServiceLifetime.Singleton,
})
export class InteractivePresentationPolicy implements PresentationPolicy {
  readonly includeSystemContextEveryTurn = false;

  // Not under --sandbox: sandboxed action execution runs in a worker process
  // without the wake scheduler or the live session, so wakes cannot be armed.
  readonly supportsScheduledWakes: boolean;

  constructor(cliInput: ParsedCliInput) {
    this.supportsScheduledWakes = !(cliInput.sandbox ?? false);
  }

  initialUserContextItems(): AIContextItem[] {
    return [];
  }

  sessionEndedMessage(): string {
    return SESSION_ENDED_START_NEW_MESSAGE;
  }

  resumedSessionEndedMessage(): string {
    return RESUMED_SESSION_ENDED_MESSAGE;
  }

  resolveGoal(action: UserAction): string {
    return promptGoal(action) ?? '';
  }
}
